import React, { useState, useRef, useMemo, useCallback } from "react";

import openToast from "../utils/sweetAlert";
import Spinner from "../components/Spinner";
import Textarea from "../components/Textarea";
import useMediaType from "../hooks/useMediaType";
import PhoneNumber from "../components/PhoneNumber";
import LoadingCenter from "../components/LoadingCenter";
import PopupCallResult from "../components/PopupCallResult";
import SocialMediaUser from "../components/SocialMediaUser";
import PopupSelectPhone from "../components/PopupSelectPhone";
import OutdatedCustomersRow2 from "../components/OutdatedCustomersRow2";
import PopupSelectSocialMedia from "../components/PopupSelectSocialMedia";
import PopupTransferCustomer from "../components/PopupTransferCustomer";

import { useForm } from "react-hook-form";
import { useHttp } from "../hooks/useHttp";
import { useAuth } from "../providers/auth";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PhoneCall, MessageCircle, Upload, Edit2, Search, Send } from "react-feather";
import { CTable, CTableRow, CTableHead, CTableBody, CTableHeaderCell } from "@coreui/react";

import * as yup from "yup";

import ChartHours from "../components/ChartHours";
import DocDropzone from "../components/DocDropzone";
import { yupResolver } from "@hookform/resolvers/yup";
import BoxCustomerMenu from "../components/BoxCustomerMenu";
import BoxProfilePicture from "../components/BoxProfilePicture";
import { Box, Popup, PopupTags } from "../components";
import ProspectPagination from "../components/Prospect/ProspectPagination";
import SwitchCheckbox from "../components/Forms/SwitchCheckbox";

const PER_PAGE = 20;

const annotation = yup.object({
  annotation: yup.string(),
  call: yup.string().required("É necessário selecionar uma opção acima!").nullable()
}).required();

function OutdatedCustomers() {
  const { user } = useAuth();
  const request = useHttp();
  const { getMediaType } = useMediaType();

  // ====== Estados gerais ======================================================
  const [callId, setCallId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [feedbackBttn, setFeedbackBttn] = useState(false);

  // Triggers ações
  const [mediaTrigger, setMediaTrigger] = useState(false);
  const [phoneTrigger, setPhoneTrigger] = useState(false);
  const [callTrigger, setCallTrigger] = useState(false);

  const page = useRef(1);
  const [pageState, setPageState] = useState(page.current);
  const [enableBttnsTemp, setEnableBttnsTemp] = useState([]);
  const inputFilesRef = useRef(null);


  // ====== Filtros (modelo único) =============================================
  const [filters, setFilters] = useState({
    term: "",         // busca por nome
    stateId: "",      // id do estado
    cpf: "",
    rg: "",
    email: "",
    consulti: "",     // modelo
    bidders: false,
    notBidders: false,
    withUsadaoId: false,
    withoutUsadaoId: false,
    tags: [],         // array de ids ou objetos {id,name}
  });

  const setFiltersWithReset = useCallback((updater) => {
    setFilters(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return { ...(next || {}) };
    });
    page.current = 1;
    setPageState(1);
  }, [setFilters, setPageState]);

  // Modal único de filtros
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [tagsPopupOpen, setTagsPopupOpen] = useState(false);

  // formulário do modal (edição antes de aplicar)
  const [draft, setDraft] = useState(filters);

  // ====== Forms não relacionados a filtros ===================================
  const { register: registerMedia, handleSubmit: handleSubmitMedia, reset: resetMedia } = useForm();
  const { register: registerPhone, handleSubmit: handleSubmitPhone, reset: resetPhone } = useForm();
  const { register: registerAnnotation, handleSubmit: handleSubmitAnnotation, reset: resetAnnotation,
    formState: { errors: errorsAnnotation } } = useForm({ resolver: yupResolver(annotation) });

  // ====== States (UFs) ========================================================
  const states = useQuery({
    queryKey: ["states"],
    queryFn: () => request({ url: `/state`, method: "GET" }),
    refetchOnWindowFocus: false
  });

  // ====== Helpers filtros =====================================================
  const buildQueryParams = () => {
    const params = new URLSearchParams();

    if (filters.term) params.set("name", filters.term);
    if (filters.stateId) params.set("regiao", filters.stateId);

    if (filters.cpf) params.set("cpf", filters.cpf.trim());
    if (filters.rg) params.set("rg", filters.rg.trim());
    if (filters.email) params.set("email", filters.email.trim());
    if (filters.consulti) params.set("modelo_consulti", filters.consulti.trim());

    if (filters.bidders) params.set("arrematantes", "1");
    if (filters.notBidders) params.set("nao_arrematantes", "1");
    if (filters.withUsadaoId) params.set("com_usadao", "1");
    if (filters.withoutUsadaoId) params.set("sem_usadao", "1");

    const tagIds = (Array.isArray(filters.tags) ? filters.tags : [])
      .map(t => typeof t === "object" ? (t.id ?? t.value ?? t) : t)
      .filter(Boolean);
    if (tagIds.length) params.set("tags", tagIds.join(","));

    return params;
  };

  const appliedFilterChips = useMemo(() => {
    const chips = [];
    if (filters.term) chips.push({ key: "term", label: `Busca: "${filters.term}"`, remove: () => setFiltersWithReset(s => ({ ...s, term: "" })) });

    if (filters.stateId) {
      const stName = states?.data?.data?.find(s => String(s.id) === String(filters.stateId))?.name ?? `Estado #${filters.stateId}`;
      chips.push({ key: "state", label: `Estado: ${stName}`, remove: () => setFiltersWithReset(s => ({ ...s, stateId: "" })) });
    }

    if (filters.cpf) chips.push({ key: "cpf", label: `CPF: ${filters.cpf}`, remove: () => setFiltersWithReset(s => ({ ...s, cpf: "" })) });
    if (filters.rg) chips.push({ key: "rg", label: `RG: ${filters.rg}`, remove: () => setFiltersWithReset(s => ({ ...s, rg: "" })) });
    if (filters.email) chips.push({ key: "email", label: `E-mail: ${filters.email}`, remove: () => setFiltersWithReset(s => ({ ...s, email: "" })) });
    if (filters.consulti) chips.push({ key: "consulti", label: `Modelo: ${filters.consulti}`, remove: () => setFiltersWithReset(s => ({ ...s, consulti: "" })) });

    const tagList = (Array.isArray(filters.tags) ? filters.tags : [])
      .map(t => (typeof t === "object" ? { id: t.id ?? t.value, name: t.name ?? t.label ?? `#${t.id ?? t.value}` } : { id: t, name: `#${t}` }));
    tagList.forEach(t => {
      chips.push({
        key: `tag-${t.id}`,
        label: `Tag: ${t.name}`,
        remove: () => setFiltersWithReset(s => ({
          ...s,
          tags: s.tags.filter(x => (typeof x === "object" ? (x.id ?? x.value) !== t.id : x !== t.id))
        }))
      });
    });

    if (filters.bidders) chips.push({ key: "arrematantes", label: "Arrematantes", remove: () => setFiltersWithReset(s => ({ ...s, bidders: false })) });
    if (filters.notBidders) chips.push({ key: "nao_arrematantes", label: "Não arrematantes", remove: () => setFiltersWithReset(s => ({ ...s, notBidders: false })) });
    if (filters.withUsadaoId) chips.push({ key: "com_usadao", label: "Com Usadão ID", remove: () => setFiltersWithReset(s => ({ ...s, withUsadaoId: false })) });
    if (filters.withoutUsadaoId) chips.push({ key: "sem_usadao", label: "Sem Usadão ID", remove: () => setFiltersWithReset(s => ({ ...s, withoutUsadaoId: false })) });

    return chips;
  }, [filters, states?.data?.data, setFiltersWithReset]);

  const filterCount = appliedFilterChips.length;

  // ====== Dados (clientes) ========================
  const customers = useQuery({
    queryKey: ["customers", pageState, filters],
    queryFn: async () => {
      const params = buildQueryParams();
      params.set("page", String(page.current || 1));
      params.set("per_page", String(PER_PAGE));

      const resp = await request({ url: `/users/allsl?${params.toString()}`, method: "GET" });
      return resp;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    onSuccess: () => setTagsPopupOpen(false)
  });

  const refetchCustomers = () => {
    setPageState(page.current);
    return customers.refetch();
  };

  const [transferTrigger, setTransferTrigger] = useState(false);
  const [transferCustomer, setTransferCustomer] = useState(null);

  const openTransferCustomer = useCallback((customer) => {
    setTransferCustomer(customer);
    setTransferTrigger(true);
  }, []);

  // ====== Mutations de ações (não filtros) ===================================
  const socialMediaMutation = useMutation({
    mutationKey: ["socialMedias"],
    mutationFn: id => request({ url: "/users/socialmedia/" + id, method: "GET" })
  });

  const takeOnCustomerMutation = useMutation({
    mutationKey: ["takeOnCustomer"],
    mutationFn: media => request({ url: "/chats/novosl", method: "POST", data: { social_media: media, user_id: customerId } }),
    onSuccess: response => {
      setMediaTrigger(false);
      setFeedbackBttn(false);
      setCallTrigger(false);
      setCallId("");
      resetMedia();
      resetPhone();
      resetAnnotation();

      if (!response.data.error) openToast("success", "Sucesso", response.data.message);
      if (response.data.error) openToast("error", "Ocorreu um erro", response.data.message);
    }
  });

  const customerHandleMutation = useMutation({
    mutationKey: ["customerHandle"],
    mutationFn: id => request({ url: "/users/list/phone/" + id, method: "GET" }),
  });

  const callMutation = useMutation({
    mutationFn: number => request({ url: "/calls/ligarsl", method: "POST", data: { user_id: customerId, phone: number, outdated: true } }),
    onSuccess: response => {
      if (response.data.error) {
        openToast("error", "Ocorreu um erro", response.data.message);
        return;
      }
      setCallId(response.data.call_id);
      setPhoneTrigger(false);
      setCallTrigger(true);
      setTimeout(() => setFeedbackBttn(true), 10000);
    }
  });

  const feedbackMutation = useMutation({
    mutationFn: (resultCall, annotationCall) =>
      request({
        url: "/users/assume/new/" + customerId, method: "POST",
        data: { call_id: callId, result_call: resultCall, annotation: annotationCall || "" }
      }),
    onSettled: () => {
      setFeedbackBttn(false);
      setCallTrigger(false);
      setCallId("");
      resetMedia();
      resetPhone();
      resetAnnotation();
    }
  });

  const { data: info, mutate, isLoading } = useMutation(["teste"], userId => request({ url: `/users/${userId}`, method: "GET" }));
  const informationFn = user => mutate(user.id);

  // ====== Handlers filtros ====================================================
  const openFilters = (e) => {
    e?.preventDefault?.();
    setDraft({ ...filters });
    setFiltersModalOpen(true);
  };

  const applyFilters = () => {
    setFiltersWithReset(draft);
    setFiltersModalOpen(false);
  };

  const clearAllFilters = () => {
    const cleared = {
      term: "",
      stateId: "",
      cpf: "",
      rg: "",
      email: "",
      consulti: "",
      bidders: false,
      notBidders: false,
      withUsadaoId: false,
      withoutUsadaoId: false,
      tags: [],
    };
    setDraft(cleared);
    setFiltersWithReset(cleared);
  };

  const removeChipInsideModal = (key, value) => {
    setDraft(prev => {
      const next = { ...prev };
      if (key === "tag") {
        next.tags = prev.tags.filter(x => (typeof x === "object" ? (x.id ?? x.value) !== value : x !== value));
      } else if (key === "stateId") {
        next.stateId = "";
      } else if (key in next) {
        next[key] = typeof next[key] === "boolean" ? false : "";
      }
      return next;
    });
  };

  // busca do header (enter aplica direto)
  const onSearchEnter = (e) => {
    if (e.key === "Enter") {
      const value = e.target.value;
      setFiltersWithReset(prev => ({ ...prev, term: value }));
    }
  };

  const handleTagsSelected = (selected) => {
    const normalized = Array.isArray(selected)
      ? selected.filter(Boolean)
      : (selected ? [selected] : []);

    setDraft(prev => ({ ...prev, tags: normalized }));

    if (!filtersModalOpen) {
      setFiltersWithReset(prev => ({ ...prev, tags: normalized }));
    }

    setTagsPopupOpen(false);
  };
  // ====== UI ==================================================================
  return (
    <div className="customer-grid">
      {/* ======= POPUPS DE AÇÃO ======= */}
      <PopupSelectSocialMedia trigger={mediaTrigger} setTrigger={setMediaTrigger}>
        {socialMediaMutation.isLoading && <LoadingCenter />}
        {!socialMediaMutation.isLoading && (
          <form onSubmit={handleSubmitMedia(data => takeOnCustomerMutation.mutate(data.social_media))}>
            <div className="row-grid">
              {socialMediaMutation.data?.data?.map((media, index) => (
                <SocialMediaUser key={index} title={media} name="social_media" value={media} register={registerMedia}>
                  {getMediaType(media)}
                </SocialMediaUser>
              ))}
            </div>
            <div className="popup-footer">
              <button className="btn btn-primary" type="submit">
                {takeOnCustomerMutation.isLoading ? <Spinner /> : "Concluir"}
              </button>
              <button className="btn btn-light mr-20" type="button" onClick={() => setMediaTrigger(false)}>Cancelar</button>
            </div>
          </form>
        )}
      </PopupSelectSocialMedia>

      <PopupSelectPhone trigger={phoneTrigger} setTrigger={setPhoneTrigger}>
        {customerHandleMutation.isLoading && <LoadingCenter />}
        <form onSubmit={handleSubmitPhone(data => callMutation.mutate(data.number))}>
          <div className="row-grid">
            {!customerHandleMutation.isLoading && customerHandleMutation.data?.data?.map((phone, index) => (
              <PhoneNumber key={index} title={phone.name} name="number" value={phone.name} qtd={phone.qtd} lastOne={String(phone.lastOne)} register={registerPhone} />
            ))}
          </div>
          <div className="popup-footer">
            <button className="btn btn-primary" type="submit" disabled={customerHandleMutation.isLoading}>
              {callMutation.isLoading ? <Spinner /> : "Ligar"}
            </button>
          </div>
        </form>
      </PopupSelectPhone>

      <PopupCallResult trigger={callTrigger} setTrigger={setCallTrigger}>
        <form onSubmit={handleSubmitAnnotation(data => {
          setEnableBttnsTemp(prev => prev.map(bttn => (bttn.id == customerId ? { id: customerId, admin_id: user.id } : bttn)));
          if (data.call == 0) {
            setFeedbackBttn(false);
            setCallTrigger(false);
            setCallId("");
            resetMedia();
            resetPhone();
            resetAnnotation();
          } else {
            if (callId) feedbackMutation.mutate(data.call, data.annotation);
            else feedbackMutation.mutate("", "");
          }
        })}>
          <span style={{ display: "block", fontSize: "16px", color: "var(--text-color)" }}>
            Você conseguiu falar com o cliente?
          </span>

          <ul className="last-access-list mt-20">
            <li>
              <label className="label-last-accesses">
                <input type="radio" name="call" value="1" {...registerAnnotation("call")} />
                <span className="text-color">Sim</span>
              </label>
            </li>
            <li>
              <label className="label-last-accesses">
                <input type="radio" name="call" value="0" {...registerAnnotation("call")} />
                <span className="text-color">Não</span>
              </label>
            </li>
          </ul>

          {errorsAnnotation?.call && <div className="text-error">{errorsAnnotation.call.message}</div>}

          <Textarea
            label="Anotação"
            name="annotation"
            type="text"
            placeholder="Insira uma anotação relacioanda a ligação (opcional)"
            error={errorsAnnotation?.annotation}
            register={registerAnnotation}
          />

          <div className="popup-footer mt-10">
            {feedbackBttn
              ? <button className="btn btn-light" type="submit">Enviar</button>
              : <button style={{ cursor: "not-allowed" }} className="btn btn-light" onClick={(e) => e.preventDefault()}>Enviar</button>
            }
          </div>
        </form>
      </PopupCallResult>

      {/* ======= POPUP DE TAGS (usado dentro do modal de filtros) ======= */}
      <PopupTags
        setSelectedTags={handleTagsSelected}
        trigger={tagsPopupOpen}
        setTrigger={setTagsPopupOpen}
        isLoading={customers.isFetching}
      
      />

      <PopupTransferCustomer
        customer={transferCustomer}
        trigger={transferTrigger}
        setTrigger={setTransferTrigger}
        onSuccess={refetchCustomers}
      />

      {/* ======= MODAL ÚNICO DE FILTROS ======= */}
      <Popup title="Filtros" trigger={filtersModalOpen} setTrigger={setFiltersModalOpen}> 
        <div
          className="flex flex-col"
          style={{ position: "relative", height: "100%", padding: "0 20px" }}
        >

          <div className="mb-15">
            <span className="text-color" style={{ opacity: 0.85 }}>Resumo</span>
            <div className="d-flex align-center flex-wrap mt-10">
              {draft.term ? <Chip label={`Busca: "${draft.term}"`} onRemove={() => removeChipInsideModal("term")} /> : null}
              {draft.stateId ? (
                <Chip
                  label={`Estado: ${states?.data?.data?.find(s => String(s.id) === String(draft.stateId))?.name ?? `#${draft.stateId}`}`}
                  onRemove={() => removeChipInsideModal("stateId")}
                />
              ) : null}
              {draft.cpf ? <Chip label={`CPF: ${draft.cpf}`} onRemove={() => removeChipInsideModal("cpf")} /> : null}
              {draft.rg ? <Chip label={`RG: ${draft.rg}`} onRemove={() => removeChipInsideModal("rg")} /> : null}
              {draft.email ? <Chip label={`E-mail: ${draft.email}`} onRemove={() => removeChipInsideModal("email")} /> : null}
              {draft.consulti ? <Chip label={`Modelo: ${draft.consulti}`} onRemove={() => removeChipInsideModal("consulti")} /> : null}

              {(draft.tags || []).map(t => {
                const id = typeof t === "object" ? (t.id ?? t.value) : t;
                const name = typeof t === "object" ? (t.name ?? t.label ?? `#${id}`) : `#${t}`;
                return <Chip key={`tag-${id}`} label={`Tag: ${name}`} onRemove={() => removeChipInsideModal("tag", id)} />;
              })}

              {draft.bidders ? <Chip label="Arrematantes" onRemove={() => removeChipInsideModal("bidders")} /> : null}
              {draft.notBidders ? <Chip label="Não arrematantes" onRemove={() => removeChipInsideModal("notBidders")} /> : null}
              {draft.withUsadaoId ? <Chip label="Com Usadão ID" onRemove={() => removeChipInsideModal("withUsadaoId")} /> : null}
              {draft.withoutUsadaoId ? <Chip label="Sem Usadão ID" onRemove={() => removeChipInsideModal("withoutUsadaoId")} /> : null}

              {!draft.term && !draft.stateId && !draft.cpf && !draft.rg && !draft.email && !draft.consulti &&
                !draft.bidders && !draft.notBidders && !draft.withUsadaoId && !draft.withoutUsadaoId &&
                (!draft.tags || draft.tags.length === 0) && (
                  <span className="text-color" style={{ opacity: 0.6 }}>Nenhum filtro selecionado.</span>
                )
              }
            </div>
          </div>

          <SectionTitle>Básico</SectionTitle>
          <div className="row" style={{ gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="input-wrapper">
              <label className="label">Nome Cliente</label>
              <input
                className="input"
                name="term"
                type="text"
                placeholder="Ex.: João"
                value={draft.term}
                onChange={e => setDraft(prev => ({ ...prev, term: e.target.value }))}
              />
            </div>

            <div className="input-wrapper">
              <label className="label">Estado</label>
              <select
                className="input"
                name="state"
                value={draft.stateId}
                onChange={e => setDraft(prev => ({ ...prev, stateId: e.target.value }))}
              >
                <option value="">Selecione...</option>
                {states?.data?.data?.map(state => (
                  <option key={state.id} value={state.id}>{state.name}</option>
                ))}
              </select>
            </div>
          </div>

          <SectionTitle>Avançado</SectionTitle>
          <div className="row-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
            <SimpleInput label="CPF" name="cpf" placeholder="00000000000" value={draft.cpf}
              onChange={e => setDraft(prev => ({ ...prev, cpf: e.target.value }))} />
            <SimpleInput label="RG" name="rg" placeholder="000000000" value={draft.rg}
              onChange={e => setDraft(prev => ({ ...prev, rg: e.target.value }))} />
            <SimpleInput label="E-mail" name="email" placeholder="email@exemplo.com" value={draft.email}
              onChange={e => setDraft(prev => ({ ...prev, email: e.target.value }))} />
            <SimpleInput label="Modelo da máquina" name="consulti" placeholder="ex.: 7810" value={draft.consulti}
              onChange={e => setDraft(prev => ({ ...prev, consulti: e.target.value }))} />
          </div>

          <SectionTitle>Sinais</SectionTitle>
          <div className="row-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)", gap: 10, padding: 10 }}>
            <Toggle label="Arrematantes" checked={!!draft.bidders}
              onChange={v => setDraft(prev => ({ ...prev, bidders: v }))} />
            <Toggle label="Não arrematantes" checked={!!draft.notBidders}
              onChange={v => setDraft(prev => ({ ...prev, notBidders: v }))} />
            <Toggle label="Com Usadão ID" checked={!!draft.withUsadaoId}
              onChange={v => setDraft(prev => ({ ...prev, withUsadaoId: v }))} />
            <Toggle label="Sem Usadão ID" checked={!!draft.withoutUsadaoId}
              onChange={v => setDraft(prev => ({ ...prev, withoutUsadaoId: v }))} />
          </div>

          {/* <SectionTitle>Tags</SectionTitle> */}
          {/* <div className="d-flex align-center justify-content-between flex-wrap">
            <div className="d-flex align-center">
              <Button title="Selecionar Tags" type="button" color="light" onClick={() => setTagsPopupOpen(true)} />
              <span className="ml-10 text-color" style={{ opacity: 0.8 }}>
                {draft.tags?.length ? `${draft.tags.length} tag(s) selecionada(s)` : "Nenhuma tag selecionada"}
              </span>
            </div>
          </div> */}
        </div>

        <div
          className="align-center justify-content-between d-flex mt-20 border-top p-3"

        >
            <button
            type="button"
              className="btn btn-light p-2 w-100"
              title="Limpar filtros"
              onClick={clearAllFilters}
            >
              Limpar Filtros</button>
            <button
              type="button"
              size="sm"
              onClick={applyFilters}
            className="btn btn-primary p-2 ms-2 w-100"
            >Aplicar Filtros</button> 

        </div>
      </Popup>


      {/* ======= CONTEÚDO PRINCIPAL ======= */}
      <div className="customer-container">
        <div className="header flex-separate border-bottom fix-top">
          <span>Clientes gerais</span>

          <div className="navigation">
            <ul className="ml-20">
              <li><a href="customers" onClick={(e) => { e.preventDefault(); clearAllFilters(); }}>Limpar filtros</a></li>
              <li><a href="customers" onClick={openFilters}>Filtros {filterCount > 0 ? `(${filterCount})` : ""}</a></li>
              <li><a href="customers" onClick={(e) => { e.preventDefault(); setTagsPopupOpen(true)}}>Selecionar Tags</a></li>
            </ul>

            <div className="inner-search ml-20" style={{ height: "45px" }}>
              <div className="search-icon"><Search size={24} /></div>
              <input
                className="search-input"
                type="text"
                placeholder="Pressione enter p/ pesquisar"
                defaultValue={filters.term}
                onKeyDown={onSearchEnter}
              />
            </div>
          </div>
        </div>

        {appliedFilterChips.length > 0 && (
          <div className="u-padding-10-all border-bottom" style={{ marginTop: "10px" }}>
            <div className="d-flex align-center flex-wrap">
              <span className="text-color" style={{ marginRight: 10, opacity: 0.8, padding: "2px 6px" }}>
                Filtros aplicados ({appliedFilterChips.length}):
              </span>
              {appliedFilterChips.map(f => (
                <div key={f.key} className="tag" style={{ display: "inline-flex", alignItems: "center", marginRight: 8, marginBottom: 8 }}>
                  <span>{f.label}</span>
                  <button
                    type="button"
                    className="btn btn-light"
                    style={{ marginLeft: 8, padding: "2px 6px" }}
                    onClick={() => { f.remove(); page.current = 1; }}
                    title="Remover filtro"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="table-container">
          {(customers.isLoading || customers.isFetching) && <LoadingCenter />}


          {(!customers.isLoading && !customers.isFetching) && <CTable hover>
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell scope="col">
                  <span className="text-color">Foto</span>
                </CTableHeaderCell>
                <CTableHeaderCell scope="col">
                  <span className="text-color">Nome completo</span>
                </CTableHeaderCell>
                <CTableHeaderCell scope="col">
                  <span className="text-color">Ultimo acesso</span>
                </CTableHeaderCell>
                <CTableHeaderCell scope="col">
                  <span className="text-color">Status</span>
                </CTableHeaderCell>
                <CTableHeaderCell scope="col">
                  <span className="text-color">Aprovação</span>
                </CTableHeaderCell>
                <CTableHeaderCell scope="col">
                  <div align="center">
                    <PhoneCall size={22} className="text-color" />
                  </div>
                </CTableHeaderCell>
                <CTableHeaderCell scope="col">
                  <div align="center">
                    <MessageCircle size={22} className="text-color" />
                  </div>
                </CTableHeaderCell>
                <CTableHeaderCell scope="col">
                  <div align="center">
                    <Send size={22} className="text-color" />
                  </div>
                </CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {(customers.data?.data?.data ?? []).map((customer, index) => (
                <OutdatedCustomersRow2
                  enableBttns={enableBttnsTemp}
                  key={index}
                  customer={customer}
                  setTriggers={{ setMediaTrigger, setPhoneTrigger }}
                  setCustomerId={setCustomerId}
                  mutations={{ socialMediaMutation, customerHandleMutation }}
                  informationFn={informationFn}
                  transferCustomer={openTransferCustomer}
                />
              ))}
            </CTableBody>
          </CTable>}

          {(!customers.isLoading && !customers.isFetching) && <ProspectPagination customers={customers.data} page={page} refetch={refetchCustomers} />}
        </div>
      </div>

      {/* ======= SIDEBAR ======= */}
      <div className="customer">
        {isLoading && <LoadingCenter />}

        {info && (
          <>
            <div className="header border-left">
              <span>Informações</span>
            </div>

            <div className="chat-user">
              <BoxProfilePicture user={info?.data} />

              {info?.data.usadao_id !== "" && (
                <>
                  <BoxCustomerMenu user={info} />
                  <Box title="Documentos" icon={<Upload size={18} />} onClick={() => inputFilesRef.current.openFiles()} defaultOpen={true}>
                    <DocDropzone ref={inputFilesRef} user={info} />
                  </Box>
                </>
              )}

              <Box title="Tags" icon={<Edit2 size={18} />} onClick={() => { }} defaultOpen={true}>
                {info?.data?.tags?.map((tag, index) => (
                  <div key={index} className="tag">
                    <span>#{tag.name}</span>
                  </div>
                ))}
              </Box>

              <Box title="Observações" icon={<Edit2 size={18} />} onClick={() => { }} defaultOpen={true}>
                {info?.data?.observations?.map((item, index) => (
                  <div key={index} className="observation-box text-color">
                    <div><span className="observation-box__author">{item.author}</span></div>
                    <p className="observation-box__body">{item.body}</p>
                  </div>
                ))}
              </Box>

              <Box title="Redes sociais" icon={<Edit2 size={18} />} onClick={() => { }} defaultOpen={false}>
                {info?.data?.socialMedias?.map((media, index) => (
                  <img key={index} src={`${media.icon}`} style={{ marginRight: "10px", marginBottom: "15px" }} />
                ))}
              </Box>

              {info?.data && <ChartHours user={info?.data.id} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ======= Componentes utilitários simples (mantêm teu visual) ======= */
function Chip({ label, onRemove }) {
  return (
    <div className="tag" style={{ marginRight: 8, marginBottom: 8, display: "inline-flex", alignItems: "center" }}>
      <span>{label}</span>
      {onRemove && (
        <button className="btn btn-light" style={{ marginLeft: 8, padding: "2px 6px" }} onClick={onRemove}>×</button>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="u-padding-10-all border-bottom" style={{ marginTop: 8, marginBottom: 12 }}>
      <strong className="text-color" style={{ opacity: 0.9 }}>{children}</strong>
    </div>
  );
}

function SimpleInput({ label, name, placeholder, value, onChange, type = "text" }) {
  return (
    <div className="input-wrapper">
      <label className="label">{label}</label>
      <input className="input" name={name} placeholder={placeholder} value={value} onChange={onChange} type={type} />
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="d-flex align-center">
      <SwitchCheckbox
        checked={!!checked}
        onChange={e => onChange(e?.target ? e.target.checked : !checked)}
        variant="success"
      />
      <span className="d-inline-block ml-10">{label}</span>
    </label>
  );
}

export default OutdatedCustomers;
